/**
 * CreateArticlesModal - Smart Modal for creating articles from keyword clusters
 *
 * This is the "Orchestrator" component that:
 * 1. Shows selected clusters with MSV, KD, intent
 * 2. Detects existing articles (duplicates)
 * 3. Provides template selection dropdown (inline, no navigation)
 * 4. Creates content directly via API with primary + secondary keywords
 *
 * User never leaves ClusterResultsPage - "Fast-Track" UX pattern
 */

import type React from "react";
import {
	useState,
	useMemo,
	useEffect,
	useCallback,
	useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
	Modal,
	Space,
	Typography,
	Tag,
	Badge,
	Button,
	Select,
	Empty,
	message,
	Progress,
	Tooltip,
	Popover,
	Checkbox,
} from "antd";
import loadingAnimation from "@/assets/loading-animation.webp";
import {
	ExternalLink,
	FileText,
	Sparkles,
	CheckCircle2,
	AlertCircle,
	Layers,
	Rocket,
	LayoutTemplate,
	Loader2,
	XCircle,
	Clock,
	Globe,
	Upload,
} from "lucide-react";
import {
	type ClusterSummary,
	INTENT_LABELS,
	INTENT_COLORS,
} from "@/types/contentPlanner";
import { blogCreationService } from "@/services/blogCreationService";
import type {
	Content,
	CreateContentData,
} from "@/components/BlogCreationCommandCenter/types";
import { useBlogTemplates } from "@/hooks/useBlogTemplates";
import {
  getWordPressPublishMode,
  shouldExportToGdocs,
  type PublishDestination,
} from './ContentPlannerWizard';
import { useWPHealthGate } from '../../hooks/useWPHealthGate';
import { WPHealthWarningDialog } from './WPHealthWarningDialog';
import { universalBlogEditorPath } from '@/utils/editorRoutes';

const { Text, Title } = Typography;

interface CreateArticlesModalProps {
	open: boolean;
	onClose: () => void;
	clusters: ClusterSummary[];
	mapId: string;
	/** Pre-selected template ID (from parent page). If provided, will be used as default. */
	initialTemplateId?: string;
}

interface ClusterWithArticle extends ClusterSummary {
	existingArticle?: Content | null;
	isChecking?: boolean;
	/** Content created in this session - for tracking status */
	createdContent?: Content | null;
}

const CreateArticlesModal: React.FC<CreateArticlesModalProps> = ({
	open,
	onClose,
	clusters,
	initialTemplateId,
}) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// State
	const [clustersWithArticles, setClustersWithArticles] = useState<
		ClusterWithArticle[]
	>([]);
	const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
		null,
	);
	const [isCreating, setIsCreating] = useState(false);
	const [hasLaunched, setHasLaunched] = useState(false);

	// Transient per-keyword curation: toggle whether a preview keyword is
	// excluded from this cluster's secondary_keywords. Stored on the local
	// cluster state (excluded_keywords); not persisted to the topical map.
	const handleToggleKeyword = (clusterId: string, keyword: string) => {
		setClustersWithArticles((prev) =>
			prev.map((c) => {
				if (c.id !== clusterId) return c;
				const excluded = new Set(c.excluded_keywords || []);
				if (excluded.has(keyword)) excluded.delete(keyword);
				else excluded.add(keyword);
				return { ...c, excluded_keywords: Array.from(excluded) };
			}),
		);
	};
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

	// Publish destination (none/wordpress/gdocs/both)
	const [publishDestination, setPublishDestination] = useState<PublishDestination>('none');

	// Fetch templates for dropdown
	const { data: templatesData, isLoading: templatesLoading } = useBlogTemplates();
	const templates = templatesData?.templates || [];

	// Get the currently selected template object for display
	const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

	// Set default template when templates load or initialTemplateId changes
	useEffect(() => {
		if (templates.length > 0 && !selectedTemplateId) {
			// Priority: initialTemplateId > popular template > first template
			if (initialTemplateId) {
				setSelectedTemplateId(initialTemplateId);
			} else {
				const popular = templates.find((t) => t.is_popular);
				setSelectedTemplateId(popular?.id || templates[0]?.id || null);
			}
		}
	}, [templates, selectedTemplateId, initialTemplateId]);

	// Update selected template when initialTemplateId changes (e.g., parent page selection)
	useEffect(() => {
		if (initialTemplateId && open) {
			setSelectedTemplateId(initialTemplateId);
		}
	}, [initialTemplateId, open]);

	// Function to check for existing articles
	const checkExistingArticles = useCallback(async () => {
		console.log("[CREATE_ARTICLES_DEBUG] checkExistingArticles called");
		console.log("[CREATE_ARTICLES_DEBUG] Input clusters:", clusters);
		console.log(
			"[CREATE_ARTICLES_DEBUG] Input clusters.length:",
			clusters.length,
		);

		setIsCheckingDuplicates(true);
		// CRITICAL FIX: Clear createdContent when re-checking to prevent stale state
		setClustersWithArticles(clusters.map((c) => ({ ...c, isChecking: true, createdContent: undefined })));

		try {
			const response = await blogCreationService.list({ page_size: 500 });
			const existingContent = response.items;

			console.log(
				"[CREATE_ARTICLES_DEBUG] Existing content count:",
				existingContent.length,
			);

			const keywordToContent = new Map<string, Content>();
			existingContent.forEach((item) => {
				const normalizedKeyword = item.keyword.toLowerCase().trim();
				keywordToContent.set(normalizedKeyword, item);
			});

			const updatedClusters = clusters.map((cluster) => {
				const normalizedName = cluster.name.toLowerCase().trim();
				const existingArticle = keywordToContent.get(normalizedName) || null;
				console.log(
					"[CREATE_ARTICLES_DEBUG] Cluster:",
					cluster.name,
					"| Has existing?",
					!!existingArticle,
				);
				return { ...cluster, existingArticle, isChecking: false };
			});

			console.log("[CREATE_ARTICLES_DEBUG] Updated clusters:", updatedClusters);
			console.log(
				"[CREATE_ARTICLES_DEBUG] Clusters with existing articles:",
				updatedClusters.filter((c) => c.existingArticle).length,
			);
			console.log(
				"[CREATE_ARTICLES_DEBUG] New clusters (no existing):",
				updatedClusters.filter((c) => !c.existingArticle).length,
			);

			setClustersWithArticles(updatedClusters);
		} catch (error) {
			console.error("Failed to check existing articles:", error);
			setClustersWithArticles(
				clusters.map((c) => ({ ...c, isChecking: false })),
			);
		} finally {
			setIsCheckingDuplicates(false);
		}
	}, [clusters]);

	// Check for existing articles when modal opens
	useEffect(() => {
		if (open && clusters.length > 0) {
			checkExistingArticles();
		}
	}, [open, clusters, checkExistingArticles]);

	// Reset state when modal closes
	useEffect(() => {
		if (!open) {
			setIsCreating(false);
			setHasLaunched(false);
			// Clear polling interval
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}
			// Note: Do NOT reset auto-publish store - jobs should persist across modal sessions
		}
	}, [open]);

	// Cleanup polling on unmount
	useEffect(() => {
		return () => {
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
			}
		};
	}, []);

	// Poll for content status updates after launching
	const pollContentStatuses = useCallback(async () => {
		const createdContentIds = clustersWithArticles
			.filter((c) => c.createdContent)
			.map((c) => c.createdContent?.id)
			.filter((id): id is string => id !== undefined);

		if (createdContentIds.length === 0) return;

		try {
			const response = await blogCreationService.list({ page_size: 500 });
			const contentMap = new Map(response.items.map((item) => [item.id, item]));

			setClustersWithArticles((prev) =>
				prev.map((cluster) => {
					if (cluster.createdContent) {
						const updatedContent = contentMap.get(cluster.createdContent.id);
						if (updatedContent) {
							return { ...cluster, createdContent: updatedContent };
						}
					}
					return cluster;
				}),
			);

			// Stop polling if all content is completed or failed
			// For auto-publish items, wait for terminal publish status too
			const allDone = clustersWithArticles.every(
				(c) =>
					!c.createdContent ||
					c.createdContent.status === "failed" ||
					(c.createdContent.status === "completed" &&
						// If not auto-publish, we're done when completed
						(!c.createdContent.publish_immediately ||
							// If auto-publish, wait for terminal publish status
							c.createdContent.publish_status === "published" ||
							c.createdContent.publish_status === "failed")),
			);
			if (allDone && pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}
		} catch (error) {
			console.error("Failed to poll content statuses:", error);
		}
	}, [clustersWithArticles]);

	// Start polling when content is launched
	useEffect(() => {
		if (hasLaunched && !pollingIntervalRef.current) {
			// NOTE: Increased from 3s to 10s to reduce HTTP/1.1 connection pressure
			// (3 users × 3s polling was creating request storms that saturated servers)
			pollingIntervalRef.current = setInterval(pollContentStatuses, 10000);
			// Also poll immediately
			pollContentStatuses();
		}
	}, [hasLaunched, pollContentStatuses]);

	// Calculate stats
	const stats = useMemo(() => {
		const newClusters = clustersWithArticles.filter((c) => !c.existingArticle);
		const existingClusters = clustersWithArticles.filter(
			(c) => c.existingArticle,
		);
		const totalMSV = clustersWithArticles.reduce(
			(sum, c) => sum + c.total_volume,
			0,
		);
		const totalKeywords = clustersWithArticles.reduce(
			(sum, c) => sum + c.keyword_count,
			0,
		);

		console.log(
			"[CREATE_ARTICLES_DEBUG] Stats recalculated - clustersWithArticles.length:",
			clustersWithArticles.length,
		);
		console.log(
			"[CREATE_ARTICLES_DEBUG] Stats - new:",
			newClusters.length,
			"| existing:",
			existingClusters.length,
		);

		return {
			total: clustersWithArticles.length,
			new: newClusters.length,
			existing: existingClusters.length,
			totalMSV,
			totalKeywords,
		};
	}, [clustersWithArticles]);

	// Get new clusters (ones without existing articles)
	const newClusters = useMemo(() => {
		const filtered = clustersWithArticles.filter((c) => !c.existingArticle);
		console.log(
			"[CREATE_ARTICLES_DEBUG] newClusters useMemo recalculated - count:",
			filtered.length,
		);
		console.log(
			"[CREATE_ARTICLES_DEBUG] newClusters:",
			filtered.map((c) => ({ id: c.id, name: c.name })),
		);
		return filtered;
	}, [clustersWithArticles]);

	/**
	 * EXECUTION: Actual article creation logic (extracted for WP health gate)
	 * Contains the API calls, state management, and result handling.
	 */
	const executeArticleCreation = async () => {
		setIsCreating(true);
		const successCount = { value: 0 };
		const failCount = { value: 0 };
		const createdContent: Map<string, Content> = new Map();

		try {
			console.log(
				"[CREATE_ARTICLES_DEBUG] Starting PARALLEL creation for",
				newClusters.length,
				"clusters",
			);

			// Create content in PARALLEL - all API calls fire simultaneously
			// This reduces total wait time from (N × 2-3s) to just ~2-3s
			const createPromises = newClusters.map((cluster) => {
				// Drop the primary name + any keywords the reviewer deselected in
				// the keyword dropdown (transient excluded_keywords).
				const excluded = new Set(cluster.excluded_keywords || []);
				const secondaryKeywords = cluster.keywords_preview?.filter(
					(kw) => kw !== cluster.name && !excluded.has(kw),
				);
				const payload: CreateContentData = {
					keyword: cluster.name, // Primary keyword
					secondary_keywords: secondaryKeywords, // Secondary keywords
					template_id: selectedTemplateId || undefined,
					source: "ai",
					target_keywords_count: (secondaryKeywords?.length || 0) + 1,
					cluster_id: cluster.id, // Link back to cluster for status sync
					publish_immediately: getWordPressPublishMode(publishDestination) === 'publish' || undefined,
					wordpress_publish_mode: getWordPressPublishMode(publishDestination),
					export_to_gdocs: shouldExportToGdocs(publishDestination) || undefined,
				};

				console.log(
					"[CREATE_ARTICLES_DEBUG] Queuing parallel request for:",
					cluster.name,
				);

				return blogCreationService
					.create(payload)
					.then((content) => ({ cluster, content, success: true as const }))
					.catch((err) => ({ cluster, error: err, success: false as const }));
			});

			// Wait for ALL requests to complete (success or failure)
			const results = await Promise.all(createPromises);

			// Process results
			for (const result of results) {
				if (result.success) {
					console.log(
						"[CREATE_ARTICLES_DEBUG] SUCCESS - Content created:",
						result.content.id,
						"for cluster:",
						result.cluster.name,
					);
					createdContent.set(result.cluster.id, result.content);
					successCount.value++;
				} else {
					console.error(
						`[CREATE_ARTICLES_DEBUG] FAILED to create content for "${result.cluster.name}":`,
						result.error,
					);
					failCount.value++;
				}
			}

			console.log(
				"[CREATE_ARTICLES_DEBUG] Parallel creation completed. Success:",
				successCount.value,
				"| Failed:",
				failCount.value,
			);

			// Update clusters with created content for status tracking
			// CRITICAL FIX: Don't use fallback - only set content created in THIS session
			setClustersWithArticles((prev) =>
				prev.map((cluster) => ({
					...cluster,
					createdContent: createdContent.get(cluster.id) ?? cluster.createdContent,
				})),
			);

			// Show result message
			if (successCount.value > 0) {
				message.success(
					`${successCount.value} article${successCount.value > 1 ? "s" : ""} started! Tracking progress below.`,
				);
			}
			if (failCount.value > 0) {
				message.warning(`${failCount.value} article(s) failed to create.`);
			}

			// Invalidate the planner caches so created rows appear even if the
			// user closes via the X. The planner table reads ['content-planner',
			// 'calendar',...]; ['blog-creation-content'] is kept for other consumers.
			queryClient.invalidateQueries({ queryKey: ["blog-creation-content"] });
			queryClient.invalidateQueries({ queryKey: ["content-planner", "calendar"] });

			// Keep modal open to show progress - set hasLaunched to start polling
			setHasLaunched(true);
		} catch (error) {
			console.error("Failed to create articles:", error);
			message.error("Failed to create articles. Please try again.");
		} finally {
			setIsCreating(false);
		}
	};

	// WP Health Gate - wraps executeArticleCreation with a health check
	const { triggerWithHealthCheck, warningProps: wpHealthWarningProps } = useWPHealthGate(executeArticleCreation);

	/**
	 * VALIDATION + GATE: Validates inputs then triggers WP health check gate
	 * The gate will call executeArticleCreation if the site is healthy (or user proceeds anyway)
	 */
	const handleCreateArticles = async () => {
		console.log("[CREATE_ARTICLES_DEBUG] handleCreateArticles called");
		console.log(
			"[CREATE_ARTICLES_DEBUG] newClusters.length:",
			newClusters.length,
		);
		console.log("[CREATE_ARTICLES_DEBUG] newClusters:", newClusters);

		if (newClusters.length === 0) {
			console.log("[CREATE_ARTICLES_DEBUG] Early return - no new clusters");
			return;
		}

		await triggerWithHealthCheck();
	};

	const handleViewArticle = (content: Content) => {
		if (content.wordpress_post_url) {
			window.open(content.wordpress_post_url, "_blank");
		} else {
			navigate(universalBlogEditorPath(content.id));
		}
	};

	const getDifficultyColor = (kd: number) => {
		if (kd <= 30) return "#52c41a";
		if (kd <= 50) return "#faad14";
		if (kd <= 70) return "#fa8c16";
		return "#f5222d";
	};

	const getDifficultyLabel = (kd: number) => {
		if (kd <= 30) return "Very easy";
		if (kd <= 50) return "Easy";
		if (kd <= 70) return "Medium";
		return "Hard";
	};

	// Template options for dropdown
	const templateOptions = useMemo(() => {
		return templates.map((t) => ({
			value: t.id,
			label: (
				<div className="flex items-center gap-2">
					{/* All templates are now auto type - coral color, auto badge */}
					<Tag
						color="coral"
						className="text-xs"
						style={{ marginRight: 0 }}
					>
						⚡ Auto
					</Tag>
					<span>{t.name}</span>
					{t.is_popular && (
						<Tag color="gold" className="text-xs">
							Popular
						</Tag>
					)}
				</div>
			),
		}));
	}, [templates]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={null}
			footer={null}
			width={700}
			className="create-articles-modal"
			maskClosable={!isCreating}
			closable={!isCreating}
		>
			{/* Header */}
			<div className="mb-6">
				<Title level={4} style={{ marginBottom: 8 }}>
					Create {stats.new > 0 ? stats.new : ""} Article
					{stats.new !== 1 ? "s" : ""} from Clusters
				</Title>
				<Text type="secondary">
					Each cluster becomes one article. Primary keyword in title, secondary
					keywords woven into content.
				</Text>
			</div>

			{/* Stats Bar */}
			<div className="flex items-center gap-6 mb-4 p-3 bg-muted rounded-lg">
				<div className="flex items-center gap-2">
					<Layers size={16} className="text-blue-500 dark:text-blue-400" />
					<Text strong>{stats.total}</Text>
					<Text type="secondary">clusters</Text>
				</div>
				<div className="flex items-center gap-2">
					<FileText size={16} className="text-green-500 dark:text-green-400" />
					<Text strong>{stats.totalKeywords}</Text>
					<Text type="secondary">keywords</Text>
				</div>
				<div className="flex items-center gap-2">
					<Sparkles size={16} className="text-teal" />
					<Text strong>{stats.totalMSV.toLocaleString()}</Text>
					<Text type="secondary">total MSV</Text>
				</div>
			</div>

			{/* Template Context Header - shows when template was selected from parent */}
			{selectedTemplate && initialTemplateId && (
				<div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/40 rounded-lg border border-green-200 dark:border-green-800 mb-3">
					<div className="flex items-center gap-2">
						{selectedTemplate.icon ? (
							<span className="text-lg">{selectedTemplate.icon}</span>
						) : (
							<LayoutTemplate size={18} className="text-green-600 dark:text-green-400" />
						)}
						<div>
							<div className="text-sm font-medium text-green-800 dark:text-green-300">
								Using template: {selectedTemplate.name}
							</div>
							{selectedTemplate.default_config?.targetWordCount && (
								<div className="text-xs text-green-600 dark:text-green-400">
									~{selectedTemplate.default_config.targetWordCount} words per
									article
								</div>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Configuration Row - Template */}
			<div className="flex items-center gap-4 mb-4 p-3 bg-blue-50 dark:bg-blue-950/40 rounded-lg border border-blue-100">
				{/* Template Selector */}
				<div className="flex items-center gap-2 flex-1">
					<LayoutTemplate size={16} className="text-blue-600 dark:text-blue-400" />
					<Text strong>Template:</Text>
					<Select
						value={selectedTemplateId}
						onChange={setSelectedTemplateId}
						style={{ width: 320 }}
						loading={templatesLoading}
						placeholder="Select template..."
						options={templateOptions}
						popupMatchSelectWidth={false}
						dropdownStyle={{ minWidth: 380 }}
					/>
				</div>
			</div>

			{/* Publish Destination */}
			<div className="p-3 bg-muted rounded-lg border border-border mb-4 space-y-2">
				<span className="text-sm font-medium text-foreground">After generation</span>
				<div className="grid grid-cols-2 gap-2">
					{([
						{ value: 'none' as const, label: 'App Draft Only', icon: <FileText size={14} /> },
						{ value: 'wp_draft' as const, label: 'WordPress Draft', icon: <Globe size={14} /> },
						{ value: 'gdocs' as const, label: 'Google Doc Only', icon: <Upload size={14} /> },
						{ value: 'wp_draft_gdocs' as const, label: 'WP Draft + Doc', icon: <Globe size={14} /> },
						{ value: 'wp_publish' as const, label: 'Publish to WP', icon: <Globe size={14} /> },
						{ value: 'wp_publish_gdocs' as const, label: 'Publish + Doc', icon: <Globe size={14} /> },
					]).map((opt) => (
						<button
							key={opt.value}
							onClick={() => !hasLaunched && setPublishDestination(opt.value)}
							disabled={hasLaunched}
							className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors text-left ${
								publishDestination === opt.value
									? 'border-teal bg-teal-light text-teal-dark font-medium'
									: 'border-border bg-card text-muted-foreground hover:border-teal/50'
							} ${hasLaunched ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
						>
							{opt.icon}
							{opt.label}
						</button>
					))}
				</div>
			</div>

			{/* Articles List */}
			<div className="mb-6">
				<Text strong className="block mb-2">
					Articles to be created:
				</Text>

				{isCheckingDuplicates ? (
					<div className="flex flex-col items-center justify-center p-8 gap-2">
						<img src={loadingAnimation} alt="Loading" className="dark:invert w-40 h-auto" />
						<span className="text-muted-foreground">
							Checking for existing articles...
						</span>
					</div>
				) : clustersWithArticles.length === 0 ? (
					<Empty description="No clusters selected" />
				) : (
					<div className="space-y-3 max-h-[280px] overflow-y-auto pr-2">
						{clustersWithArticles.map((cluster, index) => (
							<div
								key={cluster.id}
								className={`p-3 rounded-lg border ${
									cluster.existingArticle
										? "bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800"
										: "bg-card border-border"
								}`}
							>
								<div className="flex items-start justify-between gap-4">
									<div className="flex items-start gap-3">
										<div className="text-muted-foreground font-medium mt-0.5">
											#{index + 1}
										</div>
										<div className="flex-1">
											<div className="flex items-center gap-2 mb-1">
												<Text strong>{cluster.name}</Text>
												{cluster.existingArticle && (
													<CheckCircle2 size={14} className="text-green-500 dark:text-green-400" />
												)}
											</div>
											{/* Secondary keywords — checkbox dropdown to deselect
											    individual keywords (read-only once an article
											    already exists for the cluster). */}
											{(() => {
												const secondaryOptions = (
													cluster.keywords_preview || []
												).filter((k) => k !== cluster.name);
												if (secondaryOptions.length === 0) return null;
												const excluded = new Set(
													cluster.excluded_keywords || [],
												);
												const includedCount = secondaryOptions.filter(
													(k) => !excluded.has(k),
												).length;
												if (cluster.existingArticle) {
													return (
														<div className="text-xs text-muted-foreground mb-1">
															+{secondaryOptions.length} secondary:{" "}
															{secondaryOptions.slice(0, 3).join(", ")}
															{secondaryOptions.length > 3 && "..."}
														</div>
													);
												}
												return (
													<Popover
														trigger="click"
														placement="bottomLeft"
														content={
															<div style={{ maxWidth: 240 }}>
																<div className="text-xs text-muted-foreground mb-1">
																	Include as secondary keywords
																</div>
																<div
																	style={{
																		maxHeight: 200,
																		overflowY: "auto",
																		display: "flex",
																		flexDirection: "column",
																		gap: 4,
																	}}
																>
																	{secondaryOptions.map((kw) => (
																		<Checkbox
																			key={kw}
																			checked={!excluded.has(kw)}
																			onChange={() =>
																				handleToggleKeyword(
																					cluster.id,
																					kw,
																				)
																			}
																		>
																			<span className="text-xs">{kw}</span>
																		</Checkbox>
																	))}
																</div>
															</div>
														}
													>
														<button
															type="button"
															className="text-xs text-blue-600 dark:text-blue-400 mb-1 inline-flex items-center gap-1 hover:underline"
														>
															{includedCount} of {secondaryOptions.length}{" "}
															secondary keyword
															{secondaryOptions.length !== 1 ? "s" : ""} ▾
														</button>
													</Popover>
												);
											})()}
											<Space size={16} wrap>
												<Text type="secondary" className="text-xs">
													Keywords:{" "}
													<Text strong className="text-blue-600 dark:text-blue-400">
														{cluster.keyword_count}
													</Text>
												</Text>
												<Text type="secondary" className="text-xs">
													SI:{" "}
													<Tag
														color={INTENT_COLORS[cluster.primary_intent]}
														className="text-xs"
													>
														{INTENT_LABELS[cluster.primary_intent]}
													</Tag>
												</Text>
												<Text type="secondary" className="text-xs">
													MSV:{" "}
													<Text strong>
														{cluster.total_volume.toLocaleString()}
													</Text>
												</Text>
												<Text type="secondary" className="text-xs">
													KD:{" "}
													<Text
														strong
														style={{
															color: getDifficultyColor(cluster.avg_difficulty),
														}}
													>
														{getDifficultyLabel(cluster.avg_difficulty)}
													</Text>
													<Badge
														count={Math.round(cluster.avg_difficulty)}
														style={{
															backgroundColor: getDifficultyColor(
																cluster.avg_difficulty,
															),
															marginLeft: 4,
															fontSize: 10,
														}}
													/>
												</Text>
											</Space>
										</div>
									</div>

									{/* Action Button / Status */}
									<div className="flex flex-col items-end gap-1">
										{cluster.existingArticle ? (
											<Button
												type="primary"
												size="small"
												icon={<ExternalLink size={12} />}
												onClick={() => {
													if (cluster.existingArticle) {
														handleViewArticle(cluster.existingArticle);
													}
												}}
												style={{
													backgroundColor: "#52c41a",
													borderColor: "#52c41a",
												}}
											>
												View article
											</Button>
										) : cluster.createdContent ? (
											// Show content status
											<div className="flex flex-col items-end gap-1">
												{cluster.createdContent.status === "pending" && (
													<Tag
														icon={<Clock size={12} className="mr-1" />}
														color="default"
													>
														Pending
													</Tag>
												)}
												{cluster.createdContent.status === "in_progress" && (
													<>
														<Tag
															icon={
																<Loader2
																	size={12}
																	className="mr-1 animate-spin"
																/>
															}
															color="processing"
														>
															{cluster.createdContent.current_stage ||
																"Processing"}
														</Tag>
														{cluster.createdContent.progress_percentage !==
															undefined && (
															<Progress
																percent={
																	cluster.createdContent.progress_percentage
																}
																size="small"
																style={{ width: 100 }}
																strokeColor="#1890ff"
															/>
														)}
													</>
												)}
												{cluster.createdContent.status === "completed" && (
													<>
														{/* Backend-driven publish status */}
														{cluster.createdContent.publish_status ===
															"pending" && (
															<Tag
																icon={<Clock size={12} className="mr-1" />}
																color="default"
															>
																Queued for publish
															</Tag>
														)}
														{cluster.createdContent.publish_status ===
															"publishing" && (
															<Tag
																icon={
																	<Upload
																		size={12}
																		className="mr-1 animate-pulse"
																	/>
																}
																color="processing"
															>
																Publishing...
															</Tag>
														)}
														{cluster.createdContent.publish_status ===
															"published" &&
															cluster.createdContent.wordpress_post_url && (
																<Button
																	type="primary"
																	size="small"
																	icon={<Globe size={12} />}
																	onClick={() => {
																		if (cluster.createdContent?.wordpress_post_url) {
																			window.open(
																				cluster.createdContent.wordpress_post_url,
																				"_blank",
																			);
																		}
																	}}
																	style={{
																		backgroundColor: "#52c41a",
																		borderColor: "#52c41a",
																	}}
																>
																	View on WP
																</Button>
															)}
														{cluster.createdContent.publish_status ===
															"failed" && (
															<div className="flex flex-col items-end gap-1">
																<Tooltip
																	title={
																		cluster.createdContent.publish_error ||
																		"Unknown error"
																	}
																>
																	<Tag
																		icon={
																			<XCircle size={12} className="mr-1" />
																		}
																		color="error"
																		className="text-xs"
																	>
																		Publish failed
																	</Tag>
																</Tooltip>
																<Button
																	type="link"
																	size="small"
																	onClick={() => {
																		if (cluster.createdContent) {
																			handleViewArticle(cluster.createdContent);
																		}
																	}}
																	className="text-xs p-0"
																>
																	View article
																</Button>
															</div>
														)}
														{/* No publish status = not auto-publish, show view button */}
														{!cluster.createdContent.publish_status && (
															<Button
																type="primary"
																size="small"
																icon={<CheckCircle2 size={12} />}
																onClick={() => {
																	if (cluster.createdContent) {
																		handleViewArticle(cluster.createdContent);
																	}
																}}
																style={{
																	backgroundColor: "#52c41a",
																	borderColor: "#52c41a",
																}}
															>
																View article
															</Button>
														)}
													</>
												)}
												{cluster.createdContent.status === "failed" && (
													<Tag
														icon={<XCircle size={12} className="mr-1" />}
														color="error"
													>
														Failed
													</Tag>
												)}
											</div>
										) : (
											<Tag color="blue">New</Tag>
										)}
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Summary */}
			{stats.existing > 0 && (
				<div className="mb-4 p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2">
					<AlertCircle size={16} className="text-amber-500 dark:text-amber-400" />
					<Text>
						<Text strong>{stats.existing}</Text> cluster(s) already have
						articles.
						{stats.new > 0 && (
							<>
								{" "}
								Only <Text strong>{stats.new}</Text> new article(s) will be
								created.
							</>
						)}
					</Text>
				</div>
			)}

			{/* Footer */}
			<div className="flex justify-end gap-3 pt-4 border-t">
				<Button onClick={onClose} disabled={isCreating}>
					{hasLaunched ? "Close" : "Cancel"}
				</Button>
				{!hasLaunched && stats.new > 0 && (
					<Button
						type="primary"
						icon={<Rocket size={14} />}
						onClick={handleCreateArticles}
						loading={isCreating}
						disabled={isCheckingDuplicates || !selectedTemplateId}
						style={{ backgroundColor: "#22c55e", borderColor: "#22c55e" }}
					>
						{isCreating
							? "Creating..."
							: `Launch ${stats.new} Article${stats.new > 1 ? "s" : ""}`}
					</Button>
				)}
				{hasLaunched && (
					<Button
						type="primary"
						onClick={() => {
							// Refresh planner caches so the new rows show up immediately.
							queryClient.invalidateQueries({ queryKey: ["blog-creation-content"] });
							queryClient.invalidateQueries({ queryKey: ["content-planner", "calendar"] });
							// Hand off cleanly: close this modal, then route to the planner
							// table view. Highlight the first created article when we have one.
							const firstCreatedId = clustersWithArticles.find(
								(c) => c.createdContent?.id,
							)?.createdContent?.id;
							onClose();
							navigate(
								`/content/planner?view=table${firstCreatedId ? `&new=${firstCreatedId}` : ""}`,
							);
						}}
					>
						View All Content
					</Button>
				)}
			</div>
			<WPHealthWarningDialog {...wpHealthWarningProps} />
		</Modal>
	);
};

export default CreateArticlesModal;
